import { IFormProfile } from "@multiweb/types/contracts";
import { ProfileChangeType } from "@multiweb/uiShared/ProfileChangeType";

export enum FormManagerProperty {
    favoriteForm,
}

/**
 * Struktura identifikující a popisující instanci otevřeného formuláře. Slouží primárně k zobrazování
 * otevřených formulářů v hlavním menu. Je součástí rozhraní pro otvírání a zavírání formulářů.
 * Neobsahuje otevřený formulář jako takový, ten je součástí IOpenedForm.
 *
 * Protože se používá v reduxu, může obsahovat pouze primitivní typy, pole a pure objekty.
 * @includeToDoc
 */
export interface IOpenedFormInfo {
    /**
     * Jedinečný identifikátor pro otevřenou instanci formuláře. Generuje se při otevření formuláře.
     * Používá se pro identifikaci při událostech otevření a zavření formuláře a také pro akci
     * zavření formuláře.
     */
    readonly formInstanceId: string;
    /**
     * Kód otevřeného formuláře. Slouží k možnosti otevřít formuláře bez položek v menu v nových záložkách.
     */
    readonly formCode: string;
    /** Id položky menu, ze které se formulář otevřel. */
    readonly menuItemId?: number;
    /** Id profilu, pokud byl formulář otevřený s aplikovaným profilem. */
    readonly profileId?: number;
    /** Lokalizační klíč názvu formuláře. Zobrazen jen pokud není nastaven customTitle. */
    readonly titleLocKey?: string;
    /** Id záznamu, který se má vybrat v hlavním framu při dalším otevření */
    readonly selectedId?: string;
    /** Příznak, zda má být na otevřeném formuláři viditelný pouze DetailFrame */
    readonly visibleOnlyDetail?: boolean;
    /** Příznak, zda má být zobrazen v menu */
    readonly showInMenu?: boolean;
    /** Příznak, zda jsou na formuláři neuložené změny */
    readonly isEdited?: boolean;
}

/**
 * Datový interface uchovávající informace o změně titulku formuláře
 * @includeToDoc
 */
export interface ICustomTitleChangedInfo {
    /**
     * Unikátní identifikátor formuláře
     */
    readonly formInstanceId: string;
    /**
     * Text titulku formuláře
     */
    readonly customTitle: string;
}

/**
 * Datový interface uchovávající informace o změně skrytí tlačítka zavření formuláře
 * @includeToDoc
 */
export interface IHideCloseButtonChanged {
    /**
     * Unikátní identifikátor formuláře
     */
    readonly formInstanceId: string;
    /**
     * Příznak, zda se má skrýt zavírací tlačítko formuláře
     */
    readonly hideCloseButton: boolean;
}

/**
 * Data události o změně profilu formuláře
 * @includeToDoc
 */
export interface IFormProfileChanged {
    /** Typ změny profilu */
    readonly type: ProfileChangeType;
    /** Data profilu */
    readonly profile: IFormProfile;
}

/**
 * Data události o přidání nebo odebrání formuláře do nebo z oblíbených
 * @includeToDoc
 */
export interface IFavoriteFormChanged {
    /** Id položky menu */
    readonly menuId: number;
    /** Kód formuláře */
    readonly formCode: string;
    /** Zda je jako oblíbený */
    readonly isFavorite: boolean;
    /** Id vybraného profilu */
    readonly profile?: IFormProfile;
}

/**
 * Data pro serializaci profilu oblíbených formulářů
 */
export interface IFavoriteFormsProfile {
    /** Kolekce formulářů */
    readonly forms: IFavoriteFormItem[];
}

/**
 * Datová struktura položky oblíbeného formuláře
 */
export interface IFavoriteFormItem {
    /** Id položky menu */
    readonly menuId: number;
    /** Kód formuláře */
    readonly formCode: string;
    /** Id vybraného profilu */
    readonly profileId?: number;
}
